import { RpcTarget, RpcStub } from "capnweb";
import { z } from "zod";
import type { SlotKind, ItemVersion } from "./types.js";

export * from "./types.js";
export * from "./contextual_actions.js";

export const ValidatedTokenSchema = z.object({
  userId: z.string(),
  orgId: z.string().optional(),
  orgSlug: z.string().optional(),
});

export type ValidatedToken = z.infer<typeof ValidatedTokenSchema>;

export class Registry<T extends { type: string }> {
  private items: Map<string, T> = new Map();

  register(item: T): void {
    this.items.set(item.type, item);
  }

  get(type: string): T | undefined {
    return this.items.get(type);
  }

  getAll(): T[] {
    return Array.from(this.items.values());
  }
}

export interface IBlobStorage {
  put(key: string, value: ArrayBuffer | ReadableStream): Promise<void>;
  get(key: string): Promise<ArrayBuffer | null>;
}

export const ActionSchema = z.object({
  verb: z.string(),
  params: z.record(z.string(), z.unknown()),
});

export type Action = z.infer<typeof ActionSchema>;

export const ActionResultSchema = z.object({
  success: z.boolean(),
  data: z.record(z.string(), z.unknown()).optional(),
  error: z.string().optional(),
});

export type ActionResult = z.infer<typeof ActionResultSchema>;

export const ActorSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("user"),
    userId: z.string(),
  }),
  z.object({
    type: z.literal("machine"),
    apiKey: z.string(),
  }),
]);

export type Actor = z.infer<typeof ActorSchema>;

export const ActionLogEntrySchema = z.object({
  id: z.number(),
  verb: z.string(),
  params: z.record(z.string(), z.unknown()),
  data: z.record(z.string(), z.unknown()).optional(),
  success: z.boolean(),
  error: z.string().optional(),
  actor: ActorSchema.optional(),
  createdAt: z.string(),
});
export type ActionLogEntry = z.infer<typeof ActionLogEntrySchema>;

export type IntegrationKind = "channel" | "source";

export function slotKindToTooltip(kind: SlotKind): string {
  switch (kind) {
    case "text":
      return "Multi-line text content";
    case "oneline":
      return "Single line of text";
    case "micropost":
      return "Short-form post (e.g. Bluesky, Twitter)";
    case "image":
      return "Image attachment";
  }
}

export interface Paginated<T> {
  entries: T[];
  prevCursor: string | null;
  nextCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  limit: number;
}

export type Loadable<T> =
  | { isLoading: true; data?: undefined; error?: undefined }
  | { isLoading: false; data: T; error?: undefined }
  | { isLoading: false; data?: undefined; error: string };

export const ListQuerySchema = z.object({
  search: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.number().optional(),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

export const CursorSchema = z.object({
  offset: z.number(),
});
export type Cursor = z.infer<typeof CursorSchema>;

export const PaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    entries: z.array(itemSchema),
    prevCursor: z.string().nullable(),
    nextCursor: z.string().nullable(),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
    limit: z.number(),
  });

export const OrgUserSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  imageUrl: z.string().nullable(),
  role: z.string(),
});
export type OrgUser = z.infer<typeof OrgUserSchema>;

export const IntegrationSchema = z.object({
  slug: z.string(),
  niceName: z.string().nullable(),
  type: z.string(),
  enabled: z.boolean(),
  configBlobSlug: z.string().nullable(),
  createdAt: z.string(),
});

export type Integration = z.infer<typeof IntegrationSchema>;

export const SignOffStatusSchema = z.enum([
  "pending",
  "signed",
  "rejected",
  "cancelled",
  "revoked",
]);
export type SignOffStatus = z.infer<typeof SignOffStatusSchema>;

export const SignOffSchema = z.object({
  id: z.number(),
  itemSlug: z.string(),
  itemVersion: z.number(),
  kind: z.string(),
  status: SignOffStatusSchema,
  requestedActor: ActorSchema,
  assignedUserId: z.string().nullable(),
  resolvedActor: ActorSchema.nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
});
export type SignOff = z.infer<typeof SignOffSchema>;

export const PostPhaseSchema = z.enum([
  "scheduling",
  "outbox",
  "published",
  "failed",
]);
export type PostPhase = z.infer<typeof PostPhaseSchema>;

export const PostSchema = z.object({
  slug: z.string(),
  niceName: z.string().nullable(),
  itemSlug: z.string(),
  itemVersion: z.number(),
  channelSlug: z.string(),
  phase: PostPhaseSchema,
  scheduledAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Post = z.infer<typeof PostSchema>;

interface IOrgCommonApi {
  listItemVersions(query?: ListQuery): Promise<Paginated<ItemVersion>>;
  listActions(query?: ListQuery): Promise<Paginated<ActionLogEntry>>;
  listIntegrations(query?: ListQuery): Promise<Paginated<Integration>>;
  listUsers(query?: ListQuery): Promise<Paginated<OrgUser>>;
  listPosts(query?: ListQuery): Promise<Paginated<Post>>;
  listSignOffs(query?: ListQuery): Promise<Paginated<SignOff>>;
  publishPost(postSlug: string): Promise<void>;

  createBlob(dataBase64: string): Promise<string>;
  getBlob(slug: string): Promise<string | null>;
  connectIntegration(
    integrationSlug: string,
    rawConfig: Record<string, unknown>,
  ): Promise<void>;
  dangerouslyDeleteOrgData(): Promise<void>;
}

export interface IOrgApi extends IOrgCommonApi {
  executeAction(action: Action): Promise<ActionResult>;
}

export interface IOrgInternalApi extends IOrgCommonApi {
  executeAction(action: Action, actor: Actor): Promise<ActionResult>;
}

export interface IOrgProvider {
  getOrgAsActor(orgId: string, actor: Actor): OrgApi;
}

export interface ITokenValidator {
  validate(token: string): Promise<ValidatedToken>;
}

export interface IAuthedApi {
  whoami(): string;
  readonly org: IOrgApi | undefined;
}

export interface IPublicApi {
  authenticate(token: string): Promise<IAuthedApi>;
}

// Client-side types
export type PublicApiStub = RpcStub<IPublicApi>;
export type AuthedApiStub = RpcStub<IAuthedApi>;
export type OrgApiStub = RpcStub<IOrgApi>;

export class OrgApi extends RpcTarget implements IOrgApi {
  #backend: IOrgInternalApi;
  #storage: IBlobStorage;
  #actor: Actor;
  #listUsersFn: (query?: ListQuery) => Promise<Paginated<OrgUser>>;

  constructor(
    backend: IOrgInternalApi,
    storage: IBlobStorage,
    actor: Actor,
    listUsersFn?: (query?: ListQuery) => Promise<Paginated<OrgUser>>,
  ) {
    super();
    this.#backend = backend;
    this.#storage = storage;
    this.#actor = actor;
    this.#listUsersFn =
      listUsersFn ??
      (() =>
        Promise.resolve({
          entries: [],
          prevCursor: null,
          nextCursor: null,
          hasNext: false,
          hasPrev: false,
          limit: 0,
        }));
  }

  listItemVersions(query?: ListQuery): Promise<Paginated<ItemVersion>> {
    return this.#backend.listItemVersions(query);
  }

  executeAction(action: Action): Promise<ActionResult> {
    return this.#backend.executeAction(action, this.#actor);
  }

  listActions(query?: ListQuery): Promise<Paginated<ActionLogEntry>> {
    return this.#backend.listActions(query);
  }

  listIntegrations(query?: ListQuery): Promise<Paginated<Integration>> {
    return this.#backend.listIntegrations(query);
  }

  listUsers(query?: ListQuery): Promise<Paginated<OrgUser>> {
    return this.#listUsersFn(query);
  }

  listPosts(query?: ListQuery): Promise<Paginated<Post>> {
    return this.#backend.listPosts(query);
  }

  listSignOffs(query?: ListQuery): Promise<Paginated<SignOff>> {
    return this.#backend.listSignOffs(query);
  }

  publishPost(postSlug: string): Promise<void> {
    return this.#backend.publishPost(postSlug);
  }

  async createBlob(dataBase64: string): Promise<string> {
    const binaryString = atob(dataBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const data = bytes.buffer as ArrayBuffer;

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = new Uint8Array(hashBuffer);
    const slug = Array.from(hashArray)
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");

    await this.#storage.put(slug, data);
    return slug;
  }

  async getBlob(slug: string): Promise<string | null> {
    const data = await this.#storage.get(slug);
    if (!data) return null;
    const bytes = new Uint8Array(data);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  connectIntegration(
    integrationSlug: string,
    rawConfig: Record<string, unknown>,
  ): Promise<void> {
    return this.#backend.connectIntegration(integrationSlug, rawConfig);
  }

  dangerouslyDeleteOrgData(): Promise<void> {
    return this.#backend.dangerouslyDeleteOrgData();
  }
}

export class AuthedApi extends RpcTarget implements IAuthedApi {
  #validatedToken: ValidatedToken;
  #org: IOrgApi | undefined;

  constructor(validatedToken: ValidatedToken, org: IOrgApi | undefined) {
    super();
    this.#validatedToken = validatedToken;
    this.#org = org;
  }

  whoami(): string {
    return this.#validatedToken.userId;
  }

  get org(): IOrgApi | undefined {
    return this.#org;
  }
}

export class PublicApi extends RpcTarget implements IPublicApi {
  #orgProvider: IOrgProvider;
  #tokenValidator: ITokenValidator;

  constructor(orgProvider: IOrgProvider, tokenValidator: ITokenValidator) {
    super();
    this.#orgProvider = orgProvider;
    this.#tokenValidator = tokenValidator;
  }

  async authenticate(token: string): Promise<IAuthedApi> {
    const validatedToken = await this.#tokenValidator.validate(token);
    const orgId = validatedToken.orgId;
    const actor: Actor = { type: "user", userId: validatedToken.userId };
    const org = orgId
      ? this.#orgProvider.getOrgAsActor(orgId, actor)
      : undefined;
    return new AuthedApi(validatedToken, org);
  }
}
